/**
 * Fracture — Decision Red-Team analyzer.
 *
 * Fracture stress-tests a ranking that *looks* settled. It reads the raw
 * per-criterion, per-participant scores a session already produced (group_scores
 * + group_criteria weights) and computes — deterministically — three kinds of
 * fragility the published list never shows:
 *
 *   knife_edge      Two adjacent priorities separated by a razor-thin margin,
 *                   where a small shift in one criterion's weight flips them.
 *   lone_criterion  A top priority that only ranks high because of ONE criterion;
 *                   remove it and the item collapses down the list.
 *   hidden_dissent  A top priority with a buried minority that scored it near the
 *                   floor — strong opposition the mean silently averaged away.
 *
 * Detection is exact maths. The LLM only narrates the result, and we fall back to
 * deterministic copy when no model is reachable, so findings are always real.
 *
 * The pure detection core lives in supabase/functions/_shared/intelligenceCores.ts
 * (shared with the ambient edge function); this file keeps the client-side data
 * loading and batch orchestration.
 */

import { getSupabaseClient } from '../supabaseClient';
import type { RunScope, BatchStats } from '../agentRunService';
import type { AgentSettings } from '../agentSettingsService';
import { createFinding, clearActiveFindings, type NewFinding } from '../agentFindingsService';
import { narrateFinding } from './llmIntelligenceService';
import {
  detectCassandraFindings,
  CASSANDRA_PERSONA,
  MAX_FINDINGS,
  type Criterion,
  type ScoreRow,
  type SessionData,
  type CoreDraftFinding,
} from '../../../supabase/functions/_shared/intelligenceCores';

export const CASSANDRA_AGENT_TYPE = 'cassandra';

// Re-exports so existing importers of this module keep working unchanged.
export { detectCassandraFindings };
export type { SessionData };
export type DraftFinding = CoreDraftFinding;

// ── Data loading ─────────────────────────────────────────────────────────────────

async function loadSessions(workspaceId: string, scope: RunScope): Promise<SessionData[]> {
  const supabase = getSupabaseClient();

  let scoreQuery = supabase
    .from('group_scores')
    .select('session_id, item_id, criteria_id, user_id, score')
    .eq('workspace_id', workspaceId);

  if (scope.type === 'date_range') {
    if (scope.from) scoreQuery = scoreQuery.gte('created_at', scope.from);
    if (scope.to)   scoreQuery = scoreQuery.lte('created_at', scope.to);
  }

  const { data: scoreRows, error: scoreErr } = await scoreQuery.limit(20000);
  if (scoreErr || !scoreRows || scoreRows.length === 0) return [];

  const scores: ScoreRow[] = (scoreRows as Record<string, unknown>[]).map((r) => ({
    sessionId:  r.session_id as string,
    itemId:     r.item_id as string,
    criteriaId: r.criteria_id as string,
    userId:     r.user_id as string,
    score:      Number(r.score),
  }));

  const sessionIds = [...new Set(scores.map((s) => s.sessionId))];

  const { data: critRows } = await supabase
    .from('group_criteria')
    .select('id, session_id, title, weight')
    .in('session_id', sessionIds);

  const critBySession = new Map<string, Criterion[]>();
  for (const row of (critRows ?? []) as Record<string, unknown>[]) {
    const sid = row.session_id as string;
    const arr = critBySession.get(sid) ?? [];
    arr.push({ id: row.id as string, title: (row.title as string) ?? 'Criterion', weight: Number(row.weight) || 1 });
    critBySession.set(sid, arr);
  }

  const allItemIds = [...new Set(scores.map((s) => s.itemId))];
  const itemTitles = new Map<string, string>();
  if (allItemIds.length) {
    const { data: itemRows } = await supabase
      .from('items')
      .select('id, title')
      .in('id', allItemIds);
    for (const row of (itemRows ?? []) as Record<string, unknown>[]) {
      itemTitles.set(row.id as string, (row.title as string) ?? '');
    }
  }

  return sessionIds.map((sid) => ({
    sessionId: sid,
    criteria: critBySession.get(sid) ?? [],
    scores: scores.filter((s) => s.sessionId === sid),
    itemTitles,
  }));
}

// ── Batch runner ─────────────────────────────────────────────────────────────────

export async function runCassandraBatch(
  workspaceId: string,
  scope: RunScope,
  config: AgentSettings,
  onProgress: (done: number, total: number) => void,
  signal: AbortSignal,
): Promise<BatchStats> {
  const stats: BatchStats = { processed: 0, proposed: 0, skipped: 0, errors: 0 };

  const sessions = await loadSessions(workspaceId, scope);
  if (sessions.length === 0) return stats;

  // Detect across all in-scope sessions, then keep the most severe findings.
  let drafts: DraftFinding[] = [];
  for (const session of sessions) {
    stats.processed += new Set(session.scores.map((s) => s.itemId)).size;
    drafts.push(...detectCassandraFindings(session));
  }
  drafts = drafts.sort((a, b) => b.weight - a.weight).slice(0, MAX_FINDINGS);

  if (drafts.length === 0) {
    onProgress(1, 1);
    return stats;
  }

  // Replace prior active findings with this run's set.
  await clearActiveFindings(workspaceId, CASSANDRA_AGENT_TYPE);

  const total = drafts.length;
  let done = 0;
  for (const draft of drafts) {
    if (signal.aborted) break;

    const narration = await narrateFinding({
      persona: draft.persona ?? CASSANDRA_PERSONA,
      findingKind: draft.findingKind,
      evidence: draft.base.data ?? {},
      systemPrompt: config.systemPrompt,
      model: config.model,
    });

    const finding: NewFinding = {
      ...draft.base,
      summary: narration?.summary || draft.fallbackSummary,
      detail: narration?.detail || draft.fallbackDetail,
    };

    const { error } = await createFinding(workspaceId, CASSANDRA_AGENT_TYPE, finding);
    if (error) stats.errors++; else stats.proposed++;

    done++;
    onProgress(done, total);
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }

  return stats;
}
